import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { user_data, user_settings } from 'src/stores/account/user';
import type { TimeZoneInterface } from 'src/types/Chart';

/**
 * @summary Add several days or another unit type of time to date.
 * @param {(String | Number | Date)} date The given Date.
 * @param {String} type Unit type of time also supports shorthand abbreviations (d|w|h|i|s|m|y).
 * @param {Number} [value=1] Several days or another unit type.
 * @param {Boolean} [startPeriod=false] Round time to the beginning of a typical period.
 * Day (midnight), Week (Monday), Month (first day of the month), Year (first day of the year)
 * @returns {Date} date
 * @example addDate(new Date("12/31/21"), 'd', 3);
 * //=> '2022-01-03'
 */
export function addDate(
	date: string | number | Date,
	type: string,
	value: number = 1,
	startPeriod: boolean = false,
	startWeekOnSun = false,
	toUTC: boolean = false,
): Date {
	let d = toUTC ? localTimeToUTC(date) : new Date(date);
	if (/^((day|week)(s?)$|(d|w)$)/.test(type)) {
		if (/^w/.test(type)) value *= 7;
		d.setDate(d.getDate() + value);
	} else if (/^(hour(s?)$|h$)/.test(type)) {
		d.setHours(d.getHours() + value);
	} else if (/^(minute(s?)$|i$)/.test(type)) {
		d.setMinutes(d.getMinutes() + value);
	} else if (/^(second(s?)$|s$)/.test(type)) {
		d.setSeconds(d.getSeconds() + value);
	} else if (/^(month(s?)$|m$)/.test(type)) {
		d.setMonth(d.getMonth() + value);
	} else if (/^(year(s?)$|y$)/.test(type)) {
		d.setFullYear(d.getFullYear() + value);
	}
	if (startPeriod) {
		if (/^((day|week|month|year)(s?)$|(d|w|m|y)$)/.test(type)) {
			d.setHours(0, 0, 0, 0);
			if (/^(week(s?)$|w$)/.test(type)) {
				let diff = d.getDay() - (startWeekOnSun ? 0 : 1);
				if (diff < 0) diff += 7;
				d.setDate(d.getDate() - diff);
			} else if (/^(month(s?)$|m$)/.test(type)) {
				d.setDate(1);
			} else if (/^(year(s?)$|y$)/.test(type)) {
				d.setMonth(0, 1);
			}
		} else if (/^(hour(s?)$|h$)/.test(type)) {
			d.setMinutes(0, 0, 0);
		} else if (/^(minute(s?)$|i$)/.test(type)) {
			d.setSeconds(0, 0);
		}
	}
	return d;
}

/**
 * @summary Format Date into a string.
 * @param {(string | number | Date)} date The given Date.
 * @param {string} timeFormat Date time format.
 * @param {boolean} [toUTC=false] Convert the local time to UTC.
 * @returns {string}
 * @example dateFormat("01/31/22 14:56", "d-m-y h:i:s A");
 * //=> '31-01-22 2:56:00 PM'
 * @example dateFormat(1643633760000, "d M y D h:i a");
 * //=> '31 Jan 22 Mon 2:56 pm'
 */
export const dateFormat = (date: string | number | Date, timeFormat: string, toUTC: boolean = false) => {
	const d = toUTC ? localTimeToUTC(date) : new Date(date);
	let result = '';
	const timeObj: { [key: string]: string | number } = {},
		month = d.getMonth(),
		hour = d.getHours(),
		match = timeFormat.match(/(Y|y|m|M|d|h|H|i|s|D|a|A|.)/g);
	timeObj.Y = d.getFullYear();
	timeObj.y = digitFormat(timeObj.Y.toString().slice(-2));
	timeObj.m = digitFormat(month + 1);
	timeObj.M = dateLabels('months')[month];
	timeObj.d = digitFormat(d.getDate());
	timeObj.H = digitFormat(hour);
	timeObj.h = hour % 12 || 12;
	timeObj.i = digitFormat(d.getMinutes());
	timeObj.s = digitFormat(d.getSeconds());
	timeObj.D = dateLabels('days')[d.getDay()];
	timeObj.a = hour < 12 ? 'am' : 'pm';
	timeObj.A = timeObj.a.toUpperCase();
	if (!/a/i.test(timeFormat)) timeObj.h = digitFormat(timeObj.h);
	match.forEach((m) => (result += /([YymMdhHisDaA])/.test(m) ? timeObj[m] : m));
	return result;
};

export const localTimeToUTC = (date: string | number | Date) => {
	const d = new Date(date);
	return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
};

export const digitFormat = (time: string | number) => {
	time = Math.floor(Number(time));
	return String((time < 10 ? '0' : '') + time);
};

export const convertDateFormat = (date: string | number | Date, timezone = null) => {
	const timeFormat = timeFormatUS() ? 'm-d-y h:i:s A' : 'd-m-y H:i:s';
	if (timezone !== null) {
		const offset = getUTCOffset(timezone);
		let offsetTime = addDate(date, 'h', offset.hours, false, false, true);
		if (offset.minutes) {
			offsetTime = addDate(offsetTime, 'i', offset.minutes * (offset.hours < 0 ? -1 : 1));
		}
		return dateFormat(offsetTime, timeFormat);
	}
	return dateFormat(date, timeFormat);
};

export const convertDatePayment = (date: string | number | Date) => {
	const dateValue = new Date(date);

	const day = dateValue.getUTCDate();
	const month = dateValue.toLocaleString('en-GB', {
		month: 'short',
		timeZone: 'UTC',
	});
	const year = dateValue.getUTCFullYear();

	return `${day} ${get(format)(`select_option_chart_short_month_${month.toLowerCase()}`, { default: month })} ${year}`;
};

/**
 * Converts a timestamp to a formatted date string.
 *
 * @param {number} timestamp - The timestamp to convert.
 * @returns {string} The formatted date string in the format `YYYY-MM-DD HH:mm:ss`.
 */
export const timestampToDate = (timestamp: number): string => {
	return dateFormat(timestamp, 'Y-m-d H:i:s');
};

/**
 * Calculate time ago.
 * @param {(String|Date)} date Past Date in time.
 * @returns {String} Return how much time passed since date.
 * @example getTimeAgo("2021-10-01 21:24:30");
 * //=> '5 months ago'
 */
export const getTimeAgo = (date: string | number | Date) => {
	let timeAgo = '';
	const timestamp = new Date(date).getTime() / 1000,
		currentTime = new Date().getTime() / 1000,
		timeLength = [60, 60, 24, 30, 12, 10];
	let i: number,
		len = timeLength.length,
		diff = currentTime >= timestamp ? currentTime - timestamp : 0;
	for (i = 0; diff >= timeLength[i] && i < len - 1; i++) {
		diff = diff / timeLength[i];
	}
	diff = Math.round(diff);
	timeAgo = diff + ' ' + dateLabels()[i] + (diff != 1 ? 's' : '') + ' ago';
	return timeAgo;
};

export const fromMillisecondsToDays = (date1: string, date2: string) => {
	const msPerDay = 1000 * 60 * 60 * 24,
		ms = new Date(date1).getTime() - new Date(date2).getTime();
	return Math.ceil(ms / msPerDay);
};

export const getTimeFormat = (hour: number, min = 0, showMinUSFormat = true): string => {
	if (timeFormatUS()) {
		return showMinUSFormat && typeof min !== 'undefined' && !Number.isNaN(min)
			? `${hour % 12 || 12}:${digitFormat(min)} ${hour >= 12 ? 'P' : 'A'}M`
			: `${hour % 12 || 12} ${hour >= 12 ? 'P' : 'A'}M`;
	}
	return typeof min !== 'undefined' && !Number.isNaN(min)
		? `${digitFormat(hour)}:${digitFormat(min)}`
		: `${digitFormat(hour)}`;
};

export const startsWeekOnSun = () => {
	return get(user_settings)?.units?.date_format == 'us';
};

export const timeFormatUS = () => {
	return get(user_settings)?.units?.date_format == 'us';
};

export const current_time_zone = (): string => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getDateByTimeZone = (timeZone: string): Date => {
	return timeZone && time_zones.includes(timeZone)
		? new Date(new Date().toLocaleString('en-US', { timeZone }))
		: new Date();
};

export const getUTCOffset = (
	timeZone: string,
): { hours: number; minutes: number; offsetTime: string } => {
	let offsetHours = 0;
	let offsetMinutes = 0;
	let offsetTime = '0:00';
	try {
		offsetTime = Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' })
			.format()
			.split(/GMT/g)[1];
		const offset = offsetTime.split(':');
		offsetHours = Number(offset?.[0] || 0);
		offsetMinutes = Number(offset?.[1] || 0);
	} catch (e) {
		console.warn('Invalid time zone specified:', timeZone);
	}
	return { hours: offsetHours, minutes: offsetMinutes, offsetTime };
};

export const getTimeZoneOffset = (timeZone: string): number => {
	const date = new Date(getDateByTimeZone(timeZone));
	return Math.round((date.getTime() - new Date().getTime()) / (1000 * 60 * 60));
};

export const getNumberOfWeekDaysInMonth = (month: number, year: number): number[] => {
	const lastDateOfMonth = new Date(year, month + 1, 0).getDate(),
		days = Array.from({ length: 7 }, () => 0);
	for (let i = 1; i <= lastDateOfMonth; ++i) {
		const day = new Date(year, month, i).getDay();
		days[day] += 1;
	}
	return days;
};

export const getUserTimeZoneDate = (): TimeZoneInterface => {
	const tz = get(user_data).timezone;
	const date = getDateByTimeZone(tz);
	return {
		tz,
		date,
		timeZoneOffset: getTimeZoneOffset(tz),
		time: dateFormat(date, timeFormatUS() ? 'h:i A' : 'H:i'),
	};
};

export const getDailyRangeOptions = (startWeekOnSun: boolean = false) => {
	const $format = get(format),
		rangeOptions = [
			{
				title: $format('option_week', { default: 'Week' }),
				value: -1,
			},
			{
				title: $format('option_monday', { default: 'Monday' }),
				value: 1,
			},
			{
				title: $format('option_tuesday', { default: 'Tuesday' }),
				value: 2,
			},
			{
				title: $format('option_wednesday', { default: 'Wednesday' }),
				value: 3,
			},
			{
				title: $format('option_thursday', { default: 'Thursday' }),
				value: 4,
			},
			{
				title: $format('option_friday', { default: 'Friday' }),
				value: 5,
			},
			{
				title: $format('option_saturday', { default: 'Saturday' }),
				value: 6,
			},
			{
				title: $format('option_sunday', { default: 'Sunday' }),
				value: 0,
			},
		];
	if (startWeekOnSun) {
		rangeOptions.splice(1, 0, rangeOptions.splice(7, 1)[0]);
	}
	return rangeOptions;
};

const SHORT_MONTHS = [
	['select_option_chart_short_month_jan', 'Jan'],
	['select_option_chart_short_month_feb', 'Feb'],
	['select_option_chart_short_month_mar', 'Mar'],
	['select_option_chart_short_month_apr', 'Apr'],
	['select_option_chart_short_month_may', 'May'],
	['select_option_chart_short_month_jun', 'Jun'],
	['select_option_chart_short_month_jul', 'Jul'],
	['select_option_chart_short_month_aug', 'Aug'],
	['select_option_chart_short_month_sep', 'Sep'],
	['select_option_chart_short_month_oct', 'Oct'],
	['select_option_chart_short_month_nov', 'Nov'],
	['select_option_chart_short_month_dec', 'Dec'],
];

const FULL_MONTHS = [
	['select_option_chart_full_month_january', 'January'],
	['select_option_chart_full_month_february', 'February'],
	['select_option_chart_full_month_march', 'March'],
	['select_option_chart_full_month_april', 'April'],
	['select_option_chart_full_month_may', 'May'],
	['select_option_chart_full_month_june', 'June'],
	['select_option_chart_full_month_july', 'July'],
	['select_option_chart_full_month_august', 'August'],
	['select_option_chart_full_month_september', 'September'],
	['select_option_chart_full_month_october', 'October'],
	['select_option_chart_full_month_november', 'November'],
	['select_option_chart_full_month_december', 'December'],
];

export const dateLabels = (type: 'days' | 'months' | 'full_months' | 'all' = 'all') => {
	const $format = get(format);
	switch (type) {
		case 'days':
			return [
				$format('date_picker_short_week_sun', { default: 'Sun' }),
				$format('date_picker_short_week_mon', { default: 'Mon' }),
				$format('date_picker_short_week_tue', { default: 'Tue' }),
				$format('date_picker_short_week_wed', { default: 'Wed' }),
				$format('date_picker_short_week_thu', { default: 'Thu' }),
				$format('date_picker_short_week_fri', { default: 'Fri' }),
				$format('date_picker_short_week_sat', { default: 'Sat' }),
			];
		case 'months':
			return SHORT_MONTHS.map(([key, fallback]) => $format(key, { default: fallback }));
		case 'full_months':
			return FULL_MONTHS.map(([key, fallback]) => $format(key, { default: fallback }));
		default:
			return ['second', 'minute', 'hour', 'day', 'month', 'year'];
	}
};

export const getMonthsOptions = (type: 'short' | 'full' = 'full') => {
	return dateLabels(type === 'full' ? 'full_months' : 'months').map((title, value) => ({
		title,
		value,
	}));
};

export const time_zones = [
	'Africa/Abidjan',
	'Africa/Accra',
	'Africa/Addis_Ababa',
	'Africa/Algiers',
	'Africa/Asmera',
	'Africa/Bamako',
	'Africa/Bangui',
	'Africa/Banjul',
	'Africa/Bissau',
	'Africa/Blantyre',
	'Africa/Brazzaville',
	'Africa/Bujumbura',
	'Africa/Cairo',
	'Africa/Casablanca',
	'Africa/Ceuta',
	'Africa/Conakry',
	'Africa/Dakar',
	'Africa/Dar_es_Salaam',
	'Africa/Djibouti',
	'Africa/Douala',
	'Africa/El_Aaiun',
	'Africa/Freetown',
	'Africa/Gaborone',
	'Africa/Harare',
	'Africa/Johannesburg',
	'Africa/Juba',
	'Africa/Kampala',
	'Africa/Khartoum',
	'Africa/Kigali',
	'Africa/Kinshasa',
	'Africa/Lagos',
	'Africa/Libreville',
	'Africa/Lome',
	'Africa/Luanda',
	'Africa/Lubumbashi',
	'Africa/Lusaka',
	'Africa/Malabo',
	'Africa/Maputo',
	'Africa/Maseru',
	'Africa/Mbabane',
	'Africa/Mogadishu',
	'Africa/Monrovia',
	'Africa/Nairobi',
	'Africa/Ndjamena',
	'Africa/Niamey',
	'Africa/Nouakchott',
	'Africa/Ouagadougou',
	'Africa/Porto-Novo',
	'Africa/Sao_Tome',
	'Africa/Tripoli',
	'Africa/Tunis',
	'Africa/Windhoek',
	'America/Adak',
	'America/Anchorage',
	'America/Anguilla',
	'America/Antigua',
	'America/Araguaina',
	'America/Argentina/La_Rioja',
	'America/Argentina/Rio_Gallegos',
	'America/Argentina/Salta',
	'America/Argentina/San_Juan',
	'America/Argentina/San_Luis',
	'America/Argentina/Tucuman',
	'America/Argentina/Ushuaia',
	'America/Aruba',
	'America/Asuncion',
	'America/Bahia',
	'America/Bahia_Banderas',
	'America/Barbados',
	'America/Belem',
	'America/Belize',
	'America/Blanc-Sablon',
	'America/Boa_Vista',
	'America/Bogota',
	'America/Boise',
	'America/Buenos_Aires',
	'America/Cambridge_Bay',
	'America/Campo_Grande',
	'America/Cancun',
	'America/Caracas',
	'America/Catamarca',
	'America/Cayenne',
	'America/Cayman',
	'America/Chicago',
	'America/Chihuahua',
	'America/Coral_Harbour',
	'America/Cordoba',
	'America/Costa_Rica',
	'America/Creston',
	'America/Cuiaba',
	'America/Curacao',
	'America/Danmarkshavn',
	'America/Dawson',
	'America/Dawson_Creek',
	'America/Denver',
	'America/Detroit',
	'America/Dominica',
	'America/Edmonton',
	'America/Eirunepe',
	'America/El_Salvador',
	'America/Fort_Nelson',
	'America/Fortaleza',
	'America/Glace_Bay',
	'America/Godthab',
	'America/Goose_Bay',
	'America/Grand_Turk',
	'America/Grenada',
	'America/Guadeloupe',
	'America/Guatemala',
	'America/Guayaquil',
	'America/Guyana',
	'America/Halifax',
	'America/Havana',
	'America/Hermosillo',
	'America/Indiana/Knox',
	'America/Indiana/Marengo',
	'America/Indiana/Petersburg',
	'America/Indiana/Tell_City',
	'America/Indiana/Vevay',
	'America/Indiana/Vincennes',
	'America/Indiana/Winamac',
	'America/Indianapolis',
	'America/Inuvik',
	'America/Iqaluit',
	'America/Jamaica',
	'America/Jujuy',
	'America/Juneau',
	'America/Kentucky/Monticello',
	'America/Kralendijk',
	'America/La_Paz',
	'America/Lima',
	'America/Los_Angeles',
	'America/Louisville',
	'America/Lower_Princes',
	'America/Maceio',
	'America/Managua',
	'America/Manaus',
	'America/Marigot',
	'America/Martinique',
	'America/Matamoros',
	'America/Mazatlan',
	'America/Mendoza',
	'America/Menominee',
	'America/Merida',
	'America/Metlakatla',
	'America/Mexico_City',
	'America/Miquelon',
	'America/Moncton',
	'America/Monterrey',
	'America/Montevideo',
	'America/Montreal',
	'America/Montserrat',
	'America/Nassau',
	'America/New_York',
	'America/Nipigon',
	'America/Nome',
	'America/Noronha',
	'America/North_Dakota/Beulah',
	'America/North_Dakota/Center',
	'America/North_Dakota/New_Salem',
	'America/Ojinaga',
	'America/Panama',
	'America/Pangnirtung',
	'America/Paramaribo',
	'America/Phoenix',
	'America/Port-au-Prince',
	'America/Port_of_Spain',
	'America/Porto_Velho',
	'America/Puerto_Rico',
	'America/Punta_Arenas',
	'America/Rainy_River',
	'America/Rankin_Inlet',
	'America/Recife',
	'America/Regina',
	'America/Resolute',
	'America/Rio_Branco',
	'America/Santa_Isabel',
	'America/Santarem',
	'America/Santiago',
	'America/Santo_Domingo',
	'America/Sao_Paulo',
	'America/Scoresbysund',
	'America/Sitka',
	'America/St_Barthelemy',
	'America/St_Johns',
	'America/St_Kitts',
	'America/St_Lucia',
	'America/St_Thomas',
	'America/St_Vincent',
	'America/Swift_Current',
	'America/Tegucigalpa',
	'America/Thule',
	'America/Thunder_Bay',
	'America/Tijuana',
	'America/Toronto',
	'America/Tortola',
	'America/Vancouver',
	'America/Whitehorse',
	'America/Winnipeg',
	'America/Yakutat',
	'America/Yellowknife',
	'Antarctica/Casey',
	'Antarctica/Davis',
	'Antarctica/DumontDUrville',
	'Antarctica/Macquarie',
	'Antarctica/Mawson',
	'Antarctica/McMurdo',
	'Antarctica/Palmer',
	'Antarctica/Rothera',
	'Antarctica/Syowa',
	'Antarctica/Troll',
	'Antarctica/Vostok',
	'Arctic/Longyearbyen',
	'Asia/Aden',
	'Asia/Almaty',
	'Asia/Amman',
	'Asia/Anadyr',
	'Asia/Aqtau',
	'Asia/Aqtobe',
	'Asia/Ashgabat',
	'Asia/Atyrau',
	'Asia/Baghdad',
	'Asia/Bahrain',
	'Asia/Baku',
	'Asia/Bangkok',
	'Asia/Barnaul',
	'Asia/Beirut',
	'Asia/Bishkek',
	'Asia/Brunei',
	'Asia/Calcutta',
	'Asia/Chita',
	'Asia/Choibalsan',
	'Asia/Colombo',
	'Asia/Damascus',
	'Asia/Dhaka',
	'Asia/Dili',
	'Asia/Dubai',
	'Asia/Dushanbe',
	'Asia/Famagusta',
	'Asia/Gaza',
	'Asia/Hebron',
	'Asia/Hong_Kong',
	'Asia/Hovd',
	'Asia/Irkutsk',
	'Asia/Jakarta',
	'Asia/Jayapura',
	'Asia/Jerusalem',
	'Asia/Kabul',
	'Asia/Kamchatka',
	'Asia/Karachi',
	'Asia/Katmandu',
	'Asia/Khandyga',
	'Asia/Krasnoyarsk',
	'Asia/Kuala_Lumpur',
	'Asia/Kuching',
	'Asia/Kuwait',
	'Asia/Macau',
	'Asia/Magadan',
	'Asia/Makassar',
	'Asia/Manila',
	'Asia/Muscat',
	'Asia/Nicosia',
	'Asia/Novokuznetsk',
	'Asia/Novosibirsk',
	'Asia/Omsk',
	'Asia/Oral',
	'Asia/Phnom_Penh',
	'Asia/Pontianak',
	'Asia/Pyongyang',
	'Asia/Qatar',
	'Asia/Qostanay',
	'Asia/Qyzylorda',
	'Asia/Rangoon',
	'Asia/Riyadh',
	'Asia/Saigon',
	'Asia/Sakhalin',
	'Asia/Samarkand',
	'Asia/Seoul',
	'Asia/Shanghai',
	'Asia/Singapore',
	'Asia/Srednekolymsk',
	'Asia/Taipei',
	'Asia/Tashkent',
	'Asia/Tbilisi',
	'Asia/Tehran',
	'Asia/Thimphu',
	'Asia/Tokyo',
	'Asia/Tomsk',
	'Asia/Ulaanbaatar',
	'Asia/Urumqi',
	'Asia/Ust-Nera',
	'Asia/Vientiane',
	'Asia/Vladivostok',
	'Asia/Yakutsk',
	'Asia/Yekaterinburg',
	'Asia/Yerevan',
	'Atlantic/Azores',
	'Atlantic/Bermuda',
	'Atlantic/Canary',
	'Atlantic/Cape_Verde',
	'Atlantic/Faeroe',
	'Atlantic/Madeira',
	'Atlantic/Reykjavik',
	'Atlantic/South_Georgia',
	'Atlantic/St_Helena',
	'Atlantic/Stanley',
	'Australia/Adelaide',
	'Australia/Brisbane',
	'Australia/Broken_Hill',
	'Australia/Currie',
	'Australia/Darwin',
	'Australia/Eucla',
	'Australia/Hobart',
	'Australia/Lindeman',
	'Australia/Lord_Howe',
	'Australia/Melbourne',
	'Australia/Perth',
	'Australia/Sydney',
	'Europe/Amsterdam',
	'Europe/Andorra',
	'Europe/Astrakhan',
	'Europe/Athens',
	'Europe/Belgrade',
	'Europe/Berlin',
	'Europe/Bratislava',
	'Europe/Brussels',
	'Europe/Bucharest',
	'Europe/Budapest',
	'Europe/Busingen',
	'Europe/Chisinau',
	'Europe/Copenhagen',
	'Europe/Dublin',
	'Europe/Gibraltar',
	'Europe/Guernsey',
	'Europe/Helsinki',
	'Europe/Isle_of_Man',
	'Europe/Istanbul',
	'Europe/Jersey',
	'Europe/Kaliningrad',
	'Europe/Kiev',
	'Europe/Kirov',
	'Europe/Lisbon',
	'Europe/Ljubljana',
	'Europe/London',
	'Europe/Luxembourg',
	'Europe/Madrid',
	'Europe/Malta',
	'Europe/Mariehamn',
	'Europe/Minsk',
	'Europe/Monaco',
	'Europe/Moscow',
	'Europe/Oslo',
	'Europe/Paris',
	'Europe/Podgorica',
	'Europe/Prague',
	'Europe/Riga',
	'Europe/Rome',
	'Europe/Samara',
	'Europe/San_Marino',
	'Europe/Sarajevo',
	'Europe/Saratov',
	'Europe/Simferopol',
	'Europe/Skopje',
	'Europe/Sofia',
	'Europe/Stockholm',
	'Europe/Tallinn',
	'Europe/Tirane',
	'Europe/Ulyanovsk',
	'Europe/Uzhgorod',
	'Europe/Vaduz',
	'Europe/Vatican',
	'Europe/Vienna',
	'Europe/Vilnius',
	'Europe/Volgograd',
	'Europe/Warsaw',
	'Europe/Zagreb',
	'Europe/Zaporozhye',
	'Europe/Zurich',
	'Indian/Antananarivo',
	'Indian/Chagos',
	'Indian/Christmas',
	'Indian/Cocos',
	'Indian/Comoro',
	'Indian/Kerguelen',
	'Indian/Mahe',
	'Indian/Maldives',
	'Indian/Mauritius',
	'Indian/Mayotte',
	'Indian/Reunion',
	'Pacific/Apia',
	'Pacific/Auckland',
	'Pacific/Bougainville',
	'Pacific/Chatham',
	'Pacific/Easter',
	'Pacific/Efate',
	'Pacific/Enderbury',
	'Pacific/Fakaofo',
	'Pacific/Fiji',
	'Pacific/Funafuti',
	'Pacific/Galapagos',
	'Pacific/Gambier',
	'Pacific/Guadalcanal',
	'Pacific/Guam',
	'Pacific/Honolulu',
	'Pacific/Johnston',
	'Pacific/Kiritimati',
	'Pacific/Kosrae',
	'Pacific/Kwajalein',
	'Pacific/Majuro',
	'Pacific/Marquesas',
	'Pacific/Midway',
	'Pacific/Nauru',
	'Pacific/Niue',
	'Pacific/Norfolk',
	'Pacific/Noumea',
	'Pacific/Pago_Pago',
	'Pacific/Palau',
	'Pacific/Pitcairn',
	'Pacific/Ponape',
	'Pacific/Port_Moresby',
	'Pacific/Rarotonga',
	'Pacific/Saipan',
	'Pacific/Tahiti',
	'Pacific/Tarawa',
	'Pacific/Tongatapu',
	'Pacific/Truk',
	'Pacific/Wake',
	'Pacific/Wallis',
];

export type TimeIntervalType = {
	from: Date;
	to: Date;
};
